// Module 6 / Slice 5 — Vehicle detail screen.
//
// Header card with vehicle meta + 2 tabs (Positions, Assignments) loaded
// via the composite GET /crewing/vehicles/{id}/timeline endpoint.
//
// Per MODULE_6_PLAN.md §10 the SPA stays behind the
// `crewing.admin_ui.enabled` feature flag at the AppShell nav level.
//
// PATTERNS.md §2.2 (screen composition) + §2.5 (data-testid naming).

import { useQuery } from "@tanstack/react-query";
import { useParams } from "@tanstack/react-router";
import { useState } from "react";
import { crewingApiClient } from "../../api/client";
import { useI18n } from "../../i18n/I18nContext";
import { PositionsTab } from "./tabs/PositionsTab";
import { AssignmentsTab } from "./tabs/AssignmentsTab";

type TabKey = "positions" | "assignments";

export function VehicleDetailScreen(): JSX.Element {
  const { t } = useI18n();
  const { vehicleId } = useParams({
    from: "/authed/admin/crewing/vehicles/$vehicleId",
  });
  const [tab, setTab] = useState<TabKey>("positions");

  const query = useQuery({
    queryKey: ["crewing", "vehicle-timeline", vehicleId] as const,
    queryFn: async () => {
      const response = await crewingApiClient.GET(
        "/crewing/vehicles/{vehicleId}/timeline",
        { params: { path: { vehicleId } } },
      );
      if (!response.data) {
        throw new Error("crewing_vehicle_timeline_fetch_failed");
      }
      return response.data;
    },
  });

  if (query.isLoading) {
    return (
      <p data-testid="vehicle-detail-loading">{t("crewing.vehicles.loading")}</p>
    );
  }
  if (query.isError || !query.data) {
    return (
      <p
        role="alert"
        data-testid="vehicle-detail-error"
        style={{ color: "var(--rc-color-danger)" }}
      >
        {(query.error as Error | undefined)?.message ?? "Vehicle not found"}
      </p>
    );
  }
  const { vehicle, positions, assignments } = query.data;

  return (
    <div data-testid="vehicle-detail-screen">
      <header className="rc-detail-header">
        <h1 data-testid="vehicle-detail-name">{vehicle.name}</h1>
        <p className="rc-help" data-testid="vehicle-detail-identifier">
          {vehicle.identifier}
        </p>
        <div data-testid="vehicle-detail-meta">
          <span
            data-testid="vehicle-detail-status"
            className={`rc-status-badge rc-status-${vehicle.status}`}
          >
            {vehicle.status}
          </span>
          <span data-testid="vehicle-detail-org-unit">{vehicle.orgUnitId}</span>
          <span data-testid="vehicle-detail-vehicle-type">
            {vehicle.vehicleTypeId}
          </span>
          {vehicle.inServiceFrom && (
            <span data-testid="vehicle-detail-in-service-from">
              {vehicle.inServiceFrom}
            </span>
          )}
          {vehicle.outOfServiceFrom && (
            <span data-testid="vehicle-detail-out-of-service-from">
              {vehicle.outOfServiceFrom}
            </span>
          )}
        </div>
      </header>

      <nav className="rc-tabs" role="tablist" data-testid="vehicle-detail-tabs">
        {(["positions", "assignments"] as const).map((k) => (
          <button
            key={k}
            type="button"
            role="tab"
            aria-selected={tab === k}
            data-testid={`vehicle-detail-tab-${k}`}
            onClick={() => setTab(k)}
          >
            {t(`crewing.vehicles.tab.${k}`)}
          </button>
        ))}
      </nav>

      <section role="tabpanel" data-testid={`vehicle-detail-panel-${tab}`}>
        {tab === "positions" && (
          <PositionsTab vehicleId={vehicle.id} positions={positions} />
        )}
        {tab === "assignments" && (
          <AssignmentsTab vehicleId={vehicle.id} assignments={assignments} />
        )}
      </section>
    </div>
  );
}
